/**
 * Production Fetch Trace (Observability Only)
 * 
 * PURPOSE:
 * Provides trace_id injection and performance logging for all fetch requests.
 * 
 * IMPORTANT: This module does NOT cache or dedupe requests.
 * All caching and deduplication is handled by rpcClient.ts (the single source of truth).
 * 
 * This module provides:
 * - trace_id injection for all RPC requests
 * - Performance logging (DEV only)
 * - Request metrics collection
 * - Double initialization detection
 * - DEV: FreeWorld callsite tracing (when window.__TRACE_FREEWORLD__ = true)
 * 
 * This module MUST be imported before any other code runs.
 */
import * as Sentry from "@sentry/react";

// ===== CONFIGURATION =====
const BOOT_ID = Math.random().toString(36).substring(2, 10);
const RPC_ENDPOINT = '/rest/v1/rpc/';

// Debug mode: set VITE_TRACE_FIN_STATUS=true to enable verbose logging
const TRACE_ENABLED = import.meta.env.VITE_TRACE_FIN_STATUS === 'true';
const IS_DEV = import.meta.env.DEV;

// ===== FREEWORLD CALLSITE TRACING (DEV ONLY) =====
// Enable with: window.__TRACE_FREEWORLD__ = true in console
const FREEWORLD_TRACED_ENDPOINTS = [
  'get_or_create_financial_status_with_session',
  'time_periods',
  'get_freeworld_task_progress_data',
  'player_time_task_completions',
  'get_player_inventory_with_session'
];

declare global {
  interface Window {
    __TRACE_FREEWORLD__?: boolean;
  }
}

/**
 * Log callsite with stack trace for FreeWorld debugging
 */
function traceFreeworldCall(endpoint: string, url: string): void {
  if (!IS_DEV) return;
  if (typeof window === 'undefined') return;
  if (!window.__TRACE_FREEWORLD__) return;
  
  // Check if this is a traced endpoint
  const isTracedEndpoint = FREEWORLD_TRACED_ENDPOINTS.some(ep => url.includes(ep));
  if (!isTracedEndpoint) return;
  
  const timestamp = new Date().toISOString();
  const stack = new Error().stack || '';
  
  // Extract clean endpoint name
  const endpointMatch = url.match(/\/rpc\/([^?]+)|\/rest\/v1\/([^?]+)/);
  const cleanEndpoint = endpointMatch ? (endpointMatch[1] || endpointMatch[2]) : endpoint;
  
  console.groupCollapsed(`🔍 [FW_TRACE] ${cleanEndpoint} @ ${timestamp}`);
  console.log('URL:', url);
  console.log('Stack trace:');
  // Filter out fetchTrace internals from stack
  const filteredStack = stack
    .split('\n')
    .filter(line => !line.includes('fetchTrace') && !line.includes('traceFreeworldCall'))
    .slice(0, 10)
    .join('\n');
  console.log(filteredStack);
  console.groupEnd();
}

// ===== DEV METRICS (page load stats) =====
interface PageLoadMetrics {
  totalCalls: number;
  rpcCalls: number;
  errors: number;
}

const getMetrics = (): PageLoadMetrics => {
  const g = globalThis as any;
  if (!g.__fw_fetch_metrics) {
    g.__fw_fetch_metrics = { totalCalls: 0, rpcCalls: 0, errors: 0 };
  }
  return g.__fw_fetch_metrics;
};

// ===== REQUEST LOG (for DevRequestPanel) =====
export interface RequestLogEntry {
  id: string;
  timestamp: number;
  method: string;
  url: string;
  endpoint: string;
  durationMs: number;
  status: 'pending' | 'ok' | 'error';
  statusCode?: number;
  traceId?: string;
  error?: string;
}

const MAX_LOG_ENTRIES = 50;

function getRequestLog(): RequestLogEntry[] {
  const g = globalThis as any;
  if (!g.__fw_request_log) {
    g.__fw_request_log = [];
  }
  return g.__fw_request_log;
}

export function getRecentRequests(): RequestLogEntry[] {
  return [...getRequestLog()];
}

function addToRequestLog(entry: RequestLogEntry): void {
  const log = getRequestLog();
  log.unshift(entry);
  if (log.length > MAX_LOG_ENTRIES) {
    log.pop();
  }
  
  // Dispatch event for DevRequestPanel
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('fw:request', { detail: entry }));
  }
}

// ===== TRACE ID GENERATION =====
let traceCounter = 0;

export function generateTraceId(): string {
  traceCounter++;
  return `${BOOT_ID}-${Date.now().toString(36)}-${traceCounter.toString(36)}`;
}

// Global trace ID context (can be set per operation)
let currentTraceId: string | null = null;

export function setCurrentTraceId(traceId: string | null): void {
  currentTraceId = traceId;
}

export function getCurrentTraceId(): string | null {
  return currentTraceId;
}

declare global {
  interface Window {
    __bootId?: string;
    __fetchMetrics?: () => PageLoadMetrics;
    __recentRequests?: () => RequestLogEntry[];
  }
}

// Expose helpers in dev
if (typeof window !== 'undefined' && IS_DEV) {
  window.__fetchMetrics = getMetrics;
  window.__recentRequests = getRecentRequests;
}

// Check for double initialization (always log this - it's a real problem)
if (typeof window !== 'undefined') {
  if (window.__bootId) {
    console.error('[FETCH] DOUBLE APP INITIALIZATION DETECTED!');
    Sentry.captureMessage('Double app initialization detected', {
      level: 'error',
      extra: { existingBootId: window.__bootId, newBootId: BOOT_ID }
    });
  }
  window.__bootId = BOOT_ID;
}

// Extract endpoint name from URL
function extractEndpoint(url: string): string {
  try {
    const urlObj = new URL(url, window.location.origin);
    const path = urlObj.pathname;
    
    // Extract RPC name
    if (path.includes('/rpc/')) {
      const match = path.match(/\/rpc\/([^/?]+)/);
      return match ? `rpc:${match[1]}` : path;
    }
    
    // Extract table name from REST API
    if (path.includes('/rest/v1/')) {
      const match = path.match(/\/rest\/v1\/([^/?]+)/);
      return match ? `table:${match[1]}` : path;
    }
    
    return path;
  } catch {
    return url.slice(0, 50);
  }
}

// ===== INSTALL FETCH INTERCEPTOR (Observability Only) =====
if (typeof window !== 'undefined') {
  const origFetch = window.fetch.bind(window);
  
  window.fetch = async function tracedFetch(
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    const metrics = getMetrics();
    metrics.totalCalls++;
    
    // Extract URL and method
    let url: string;
    let method: string;
    
    if (input instanceof Request) {
      url = input.url;
      method = (input.method || 'GET').toUpperCase();
    } else if (input instanceof URL) {
      url = input.href;
      method = (init?.method || 'GET').toUpperCase();
    } else {
      url = input;
      method = (init?.method || 'GET').toUpperCase();
    }
    
    // Check if this is an RPC call
    const isRpc = url.includes(RPC_ENDPOINT);
    if (isRpc) {
      metrics.rpcCalls++;
    }
    
    // Generate trace ID and log entry
    const traceId = currentTraceId || generateTraceId();
    const requestId = `${BOOT_ID}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const startTime = Date.now();
    const endpoint = extractEndpoint(url);
    
    // Create log entry
    const logEntry: RequestLogEntry = {
      id: requestId,
      timestamp: startTime,
      method,
      url,
      endpoint,
      durationMs: 0,
      status: 'pending',
      traceId
    };
    
    addToRequestLog(logEntry);
    
    // DEV: FreeWorld callsite tracing
    traceFreeworldCall(endpoint, url);
    
    // Add Sentry breadcrumb for RPC calls
    if (isRpc) {
      Sentry.addBreadcrumb({
        category: 'fetch',
        message: endpoint,
        level: 'info',
        data: { traceId, bootId: BOOT_ID }
      });
    }
    
    try {
      const response = await origFetch(input, init);
      
      // Update log entry
      logEntry.durationMs = Date.now() - startTime;
      logEntry.status = response.ok ? 'ok' : 'error';
      logEntry.statusCode = response.status;
      
      if (!response.ok) {
        metrics.errors++;
        logEntry.error = `HTTP ${response.status}`;
      }
      
      if (TRACE_ENABLED && isRpc) {
        console.log(`[FETCH] ${method} ${endpoint} → ${response.status} (${logEntry.durationMs}ms) trace=${traceId}`);
      }
      
      return response;
    } catch (error) {
      // Update log entry
      logEntry.durationMs = Date.now() - startTime;
      logEntry.status = 'error';
      logEntry.error = error instanceof Error ? error.message : String(error);
      metrics.errors++;
      
      if (TRACE_ENABLED) {
        console.error(`[FETCH] ${method} ${endpoint} → ERROR (${logEntry.durationMs}ms) trace=${traceId}`, error);
      }
      
      throw error;
    }
  };
  
  if (TRACE_ENABLED) {
    console.log('[FETCH] Trace interceptor installed (observability only)', { bootId: BOOT_ID });
  }
  
  // Log metrics summary after page load (DEV only)
  if (IS_DEV) {
    setTimeout(() => {
      const m = getMetrics();
      console.log(`[FETCH] Page load stats: ${m.totalCalls} total, ${m.rpcCalls} RPC, ${m.errors} errors`);
    }, 3000);
  }
}

export { BOOT_ID, TRACE_ENABLED };
